import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Globe, Hand, Check, AlertTriangle,
  ExternalLink, Loader2, ChevronDown
} from 'lucide-react';

const MODES = [
  {
    id: 'interactive',
    name: 'Interactive Mode',
    icon: Globe,
    description: 'Audience can interact freely',
    tooltip: 'Let your audience interact with your website. They can click, scroll, and explore freely.'
  },
  {
    id: 'static',
    name: 'Static Mode',
    icon: Hand,
    description: 'Host controls the view',
    tooltip: 'You control what your audience sees. Perfect for walkthroughs and guided demos.'
  }
];

const POSITIONS = [
  { id: 'fullscreen', name: 'Fullscreen', description: 'Cover entire stage' },
  { id: 'side_left', name: 'Left Side', description: 'Left panel' },
  { id: 'side_right', name: 'Right Side', description: 'Right panel' },
  { id: 'top', name: 'Top', description: 'Top strip' },
  { id: 'bottom', name: 'Bottom', description: 'Bottom strip' }
];

// Custom Select Component - uses portal to avoid overflow clipping
const Select = ({ value, onChange, options, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.name || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => { onChange(option.id); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.id === value ? 'text-purple-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default function WebOverlayModal({
  isOpen,
  onClose,
  onSave,
  overlay,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    mode: 'interactive',
    position: 'fullscreen',
    folder_id: currentFolder?.id || '',
    styling: {
      border_enabled: false,
      border_radius: 8,
      shadow: true,
      opacity: 1
    }
  });

  const [urlError, setUrlError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [urlValid, setUrlValid] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (overlay) {
        setFormData({
          ...overlay,
          folder_id: overlay.folder_id || currentFolder?.id || ''
        });
        setUrlValid(true);
      } else {
        setFormData({
          name: '',
          url: '',
          mode: 'interactive',
          position: 'fullscreen',
          folder_id: currentFolder?.id || '',
          styling: {
            border_enabled: false,
            border_radius: 8,
            shadow: true,
            opacity: 1
          }
        });
        setUrlValid(false);
      }
      setUrlError('');
      setHasInitialized(true);
    }
  }, [isOpen, overlay, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const validateUrl = (url) => {
    if (!url) {
      setUrlError('');
      setUrlValid(false);
      return;
    }

    // Check if it's a valid URL format
    try {
      const parsed = new URL(url);

      // Must be HTTPS
      if (parsed.protocol !== 'https:') {
        setUrlError('Please enter a valid HTTPS webpage.');
        setUrlValid(false);
        return;
      }

      setUrlError('');
      setUrlValid(true);
    } catch {
      setUrlError('Please enter a valid HTTPS webpage.');
      setUrlValid(false);
    }
  };

  const handleUrlChange = (url) => {
    setFormData(prev => ({ ...prev, url }));

    // Debounce validation
    clearTimeout(window.urlValidationTimeout);
    window.urlValidationTimeout = setTimeout(() => {
      validateUrl(url);
    }, 300);
  };

  const handleSave = () => {
    if (!skipFolderSelect && !formData.folder_id) return;
    if (!formData.name || !formData.url || !urlValid) return;
    onSave(formData);
    onClose();
  };

  const isFormValid = (skipFolderSelect || formData.folder_id) && formData.name && formData.url && urlValid;

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {overlay ? 'Edit Web Overlay' : 'Add Web Overlay'}
                </h2>
                <p className="text-xs text-gray-400">Embed a webpage on your stage</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {readOnly && (
              <div className="flex items-start gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            {/* Folder Selection - only show if not skipping */}
            {!skipFolderSelect && folders?.length > 0 && (
              <div>
                <label className="text-white font-medium mb-2 block">
                  Folder <span className="text-red-400">*</span>
                </label>
                <Select
                  value={formData.folder_id}
                  onChange={(v) => setFormData(prev => ({ ...prev, folder_id: v }))}
                  options={folders.map(f => ({ id: f.id, name: f.name }))}
                  placeholder="Select a folder..."
                />
              </div>
            )}
            {!skipFolderSelect && (!folders || folders.length === 0) && (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0" />
                <p className="text-xs text-yellow-300">
                  You need at least one folder before adding an overlay. Please create a folder in your Elements Library first.
                </p>
              </div>
            )}

            {/* URL Input */}
            <div>
              <label className="text-white font-medium mb-2 block">
                Webpage to Share <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  value={formData.url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="https://example.com"
                  disabled={readOnly}
                  className={`w-full px-3 py-2 bg-white/5 border rounded-lg text-white text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                    urlError ? 'border-red-500' : urlValid ? 'border-green-500' : 'border-white/10'
                  } ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isValidating && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
                  {!isValidating && urlValid && <Check className="w-4 h-4 text-green-500" />}
                  {!isValidating && urlError && <AlertTriangle className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              {urlError && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {urlError}
                </p>
              )}
              {formData.url && urlValid && (
                <a
                  href={formData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#6a1fbf] mt-1 flex items-center gap-1 hover:underline"
                >
                  <ExternalLink className="w-3 h-3" />
                  Preview in new tab
                </a>
              )}
            </div>

            {/* Mode Selector */}
            <div>
              <label className="text-white font-medium mb-2 block">Display Mode</label>
              <div className="grid grid-cols-2 gap-3">
                {MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isSelected = formData.mode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setFormData(prev => ({ ...prev, mode: mode.id }))}
                      className={`p-4 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                      title={mode.tooltip}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-[#6a1fbf]'}`} />
                        <span className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                          {mode.name}
                        </span>
                      </div>
                      <p className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>
                        {mode.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Position Selector */}
            <div>
              <label className="text-white font-medium mb-2 block">Position</label>
              <div className="grid grid-cols-5 gap-2">
                {POSITIONS.map((pos) => (
                  <button
                    key={pos.id}
                    onClick={() => setFormData(prev => ({ ...prev, position: pos.id }))}
                    className={`p-2 rounded-lg text-center transition-all ${
                      formData.position === pos.id
                        ? 'bg-[#6a1fbf] text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                    title={pos.description}
                  >
                    <span className="text-xs font-medium">{pos.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="text-white font-medium mb-2 block">
                Name Your Overlay <span className="text-red-400">*</span>
              </label>
              <input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Product Demo, Pricing Page"
                disabled={readOnly}
                className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                onClick={handleSave}
                disabled={!isFormValid}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4" />
                {overlay ? 'Save Changes' : 'Add Overlay'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
